import { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { Calendar, Export, Eye } from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { getBuyerTransactions } from '../../../../redux/services/paymentsService';
import { DillTable, DillButton, type DillColumn } from '../../../general/Dill';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';

interface BuyerInvoiceReportRow {
  key: string;
  paymentId: string;
  invoiceId: string;
  jobId: string;
  invoiceNumber: string;
  amountPaid: number;
  discountApplied: number;
  amount: number;
  createdAt: string;
  supplierName: string;
  creditUsed: number;
  paidVia: string;
}

const COLUMN_LABELS: Record<string, string> = {
  paymentId: 'Payment ID',
  invoiceId: 'Invoice ID',
  invoiceNumber: 'Invoice #',
  amountPaid: 'Amount Paid',
  discountApplied: 'Discount',
  amount: 'Invoice Amount',
  createdAt: 'Payment Date',
  supplierName: 'Supplier',
  creditUsed: 'Credit Used',
  paidVia: 'Paid Via',
};

const textColumn = (
  key: keyof BuyerInvoiceReportRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<BuyerInvoiceReportRow> => ({
  key,
  label,
  width: options?.width ?? 140,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  render: (value: string) => <div className="text-xs text-gray-500">{value || '—'}</div>,
});

const numericColumn = (
  key: keyof BuyerInvoiceReportRow,
  label: string,
  width = 150
): DillColumn<BuyerInvoiceReportRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-xs text-gray-300 text-right">—</div>;
    }
    return (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        {value < 0 ? '-' : ''}$
        {Math.abs(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

const getColumns = (hasJobs: boolean): DillColumn<BuyerInvoiceReportRow>[] => [
  textColumn('paymentId', 'Payment ID', { defaultHidden: true, width: 220 }),
  textColumn('invoiceId', 'Invoice ID', { defaultHidden: true, width: 240 }),
  textColumn('invoiceNumber', 'Invoice #', { width: 130 }),
  numericColumn('amountPaid', 'Amount Paid', 130),
  numericColumn('discountApplied', 'Discount', 120),
  numericColumn('amount', 'Invoice Amount', 140),
  textColumn('jobId', 'Job ID', { defaultHidden: !hasJobs, width: 140 }),
  {
    key: 'createdAt',
    label: 'Payment Date',
    width: 120,
    sortable: true,
    filterable: true,
    filterType: 'dateRange',
    toggleable: true,
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      return <div className="text-xs text-gray-500">{moment(value).format('MM/DD/YYYY')}</div>;
    },
  },
  textColumn('supplierName', 'Supplier', { width: 180 }),
  numericColumn('creditUsed', 'Credit Used', 130),
  textColumn('paidVia', 'Paid Via', { width: 130 }),
];

const PaymentReport = () => {
  const dispatch = useAppDispatch();
  const [selectedMainBuyerId] = useParamMainBuyerId();

  const { buyerTransactions, loadingList } = useAppSelector((state) => state.payments);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTimezone = momentTz.tz.zone(timezone) ? timezone : 'America/Los_Angeles';

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(),
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);

  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const isLoading = loadingList.includes('getBuyerTransactions');

  useEffect(() => {
    if (!selectedMainBuyerId || !dateRange.startDate || !dateRange.endDate) return;
    dispatch(
      getBuyerTransactions({
        mainBuyerId: selectedMainBuyerId,
        startDate: moment(dateRange.startDate).format('MMM DD, YYYY'),
        endDate: moment(dateRange.endDate).format('MMM DD, YYYY'),
        timezone: validTimezone,
      })
    );
  }, [selectedMainBuyerId, dateRange.startDate, dateRange.endDate, dispatch, validTimezone]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(e.target as Node)) {
        setIsColumnsOpen(false);
      }
    };
    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExportOpen, isColumnsOpen]);

  const reportRows: BuyerInvoiceReportRow[] = useMemo(() => {
    const rows: BuyerInvoiceReportRow[] = [];
    buyerTransactions.forEach((transaction) => {
      const supplierName = (transaction as any).recipient?.name ?? '';
      (transaction.invoicePayments ?? []).forEach((inv: any, idx: number) => {
        rows.push({
          key: `${transaction.id}-${idx}`,
          paymentId: transaction.id ?? '',
          jobId: inv.jobId ?? '',
          invoiceId: inv.quickbooksInvoiceId ?? inv.providerInvoiceId ?? '',
          invoiceNumber: inv.invoiceNumber ?? '',
          amountPaid: inv.amountPaid ?? 0,
          discountApplied: inv.discountAmount ?? 0,
          amount: inv.amount ?? 0,
          createdAt: transaction.createdAt ? new Date(transaction.createdAt).toISOString() : '',
          supplierName,
          creditUsed: inv.amountPaidWithCredits ?? 0,
          paidVia: transaction.isExternalPayment ? 'Outside Dill' : 'Dill',
        });
      });
    });
    return rows;
  }, [buyerTransactions]);

  const hasJobs = useMemo(() => reportRows.some((r) => !!r.jobId), [reportRows]);
  const allColumns = useMemo(() => getColumns(hasJobs), [hasJobs]);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  useEffect(() => {
    setHiddenCols((prev) => {
      if (hasJobs && prev.has('jobId')) {
        const next = new Set(prev);
        next.delete('jobId');
        return next;
      }
      if (!hasJobs && !prev.has('jobId')) {
        return new Set([...prev, 'jobId']);
      }
      return prev;
    });
  }, [hasJobs]);

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  const formatDateRangeDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) return 'Select date range';
    const start = moment(dateRange.startDate).format('MMM DD, YYYY');
    const end = moment(dateRange.endDate).format('MMM DD, YYYY');
    return `${start} – ${end}`;
  };

  const totalAmountPaid = useMemo(
    () => reportRows.reduce((sum, r) => sum + r.amountPaid, 0),
    [reportRows]
  );

  const exportRows = () =>
    reportRows.map((row) => {
      const exported: Record<string, any> = {};
      visibleColumns.forEach((col) => {
        if (col.key === 'key') return;
        exported[COLUMN_LABELS[col.key] ?? col.key] = (row as any)[col.key];
      });
      return exported;
    });

  const handleExportXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Report');
    XLSX.writeFile(wb, 'buyer-payment-report.xlsx');
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    const csv = Papa.unparse(exportRows());
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'buyer-payment-report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsExportOpen(false);
  };

  const columnsDropdown = (
    <div className="relative" ref={columnsRef}>
      <DillButton
        label="Columns"
        onClick={() => setIsColumnsOpen(!isColumnsOpen)}
        icon={<Eye size={14} />}
        variant="ghost"
      />
      {isColumnsOpen && (
        <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
            <span>Show/Hide Columns</span>
            <button
              onClick={() => setHiddenCols(new Set())}
              className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
              Select All
            </button>
          </div>
          {allColumns.map((col) => {
            const isVisible = !hiddenCols.has(col.key);
            const canHide = isVisible && visibleColumns.length <= 3;
            return (
              <label
                key={col.key}
                className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                  canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                }`}>
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={() => toggleColumn(col.key)}
                  disabled={canHide}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                />
                <span className="flex-1">{col.label}</span>
                {canHide && <span className="text-[10px] text-gray-400">Min 3 required</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );

  const exportDropdown = (
    <div
      className="relative"
      ref={exportRef}
      title={isLoading ? 'Still loading invoice payments…' : undefined}>
      <DillButton
        label="Export"
        onClick={() => setIsExportOpen(!isExportOpen)}
        icon={<Export size={14} />}
        variant="primary"
        isDisabled={isLoading}
      />
      {isExportOpen && (
        <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
          <button
            onClick={handleExportXlsx}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
            Export as XLSX
          </button>
          <button
            onClick={handleExportCsv}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
            Export as CSV
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 px-4 sm:px-6 pt-4 sm:pt-6 pb-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-800">Payment Report</h2>
            <p className="text-xs text-gray-500 mt-1">
              View invoice-level payment history with your suppliers. Select a date range to see
              total spend over any period.
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setIsDatePickerOpen(true)}
              className="inline-flex items-center gap-2 h-[34px] px-3.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
              <Calendar size={16} weight="bold" />
              <span>{formatDateRangeDisplay()}</span>
            </button>
          </div>
        </div>

        {/* Summary bar */}
        {reportRows.length > 0 && (
          <div className="mx-4 sm:mx-6 mb-4 px-4 py-3 bg-blue-50 rounded-lg flex items-center gap-4 sm:gap-6">
            <div>
              <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
                Invoices
              </p>
              <p className="text-sm font-semibold text-blue-800">{reportRows.length}</p>
            </div>
            <div className="w-px h-8 bg-blue-200" />
            <div>
              <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
                Total Paid
              </p>
              <p className="text-sm font-semibold text-blue-800">
                $
                {totalAmountPaid.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="px-2 sm:px-4 pb-4">
          <DillTable<BuyerInvoiceReportRow>
            columns={visibleColumns}
            data={reportRows}
            rowKey={(r) => r.key}
            verticalDividers
            search
            searchPlaceholder="Search invoice payments…"
            pagination
            pageSize={25}
            rowHeight={35}
            scrollX
            showActiveFilters
            striped={false}
            maxHeight="calc(100vh - 300px)"
            loading={isLoading}
            loadingText="Loading invoice payments..."
            emptyMessage="No invoice payments match your filters"
            showCount={false}
            toolbarRight={
              <div className="flex gap-2">
                {columnsDropdown}
                {exportDropdown}
              </div>
            }
          />
        </div>
      </div>

      <AppDateRangePickerModal
        open={isDatePickerOpen}
        handleClose={() => setIsDatePickerOpen(false)}
        onSelectDateRange={(range) => {
          setDateRange({
            startDate: range.startDate ?? dateRange.startDate,
            endDate: range.endDate ?? dateRange.endDate,
          });
          setIsDatePickerOpen(false);
        }}
        selectedDateRange={dateRange}
      />
    </div>
  );
};

export default PaymentReport;
